/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { useAuth } from '@clerk/clerk-react';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router';

import { Button } from '../ui/button';
import { Dialog, DialogTrigger } from '../ui/dialog';

import SubscriptionDialog from './SubscriptionDialog';
import SubscriptionsTable from './SubscriptionsTable';

import { cn } from '@/lib/utils';

export default function SubscriptionsSettings() {
    const slackInstallUrl = `${import.meta.env.VITE_SLACK_APP_URL}/slack/install`;
    const [open, setOpen] = useState(false);
    const { userId } = useAuth();
    const [notification, setNotification] = useState<{
        type: 'success' | 'error' | null;
        message: string;
    }>({ type: null, message: '' });

    const showNotificationFromUrlParams = () => {
        const params = new URLSearchParams(window.location.search);
        const status = params.get('status');
        const channel = params.get('channel');

        if (status) {
            if (status === 'success') {
                const message = channel
                    ? `Slack integration was successfully connected to the ${channel} channel.`
                    : 'Slack integration was successfully connected.';
                setNotification({ type: 'success', message });
            } else if (status === 'error') {
                setNotification({ type: 'error', message: 'Failed to connect to Slack.' });
            }

            const url = new URL(window.location.href);
            url.searchParams.delete('status');
            url.searchParams.delete('channel');
            window.history.replaceState({}, '', url);
        }
    };

    useEffect(() => {
        showNotificationFromUrlParams();
    }, []);

    // In dev, we open the slack app install URL in a new tab
    // with the token as a query parameter
    const handleSlackInstall = () => {
        try {
            const url = new URL(slackInstallUrl);
            url.searchParams.append('user-id', userId ?? 'unknown');
            window.open(url.toString(), '_blank');
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error getting auth token:', error);
        }
    };

    return (
        <div className="flex flex-col mt-10">
            {notification.type && (
                <div
                    className={cn(
                        'mb-6 p-4 rounded-md flex items-center',
                        notification.type === 'success'
                            ? 'bg-green-50 border border-green-200'
                            : 'bg-red-50 border border-red-200',
                    )}
                >
                    {notification.type === 'success' ? (
                        <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                    ) : (
                        <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                    )}
                    <span
                        className={cn(
                            'text-sm font-medium',
                            notification.type === 'success' ? 'text-green-800' : 'text-red-800',
                        )}
                    >
                        {notification.message}
                    </span>
                    <Button
                        variant="ghost"
                        size="sm"
                        className="ml-auto p-1 h-auto"
                        onClick={() => setNotification({ type: null, message: '' })}
                    >
                        <span className="sr-only">Dismiss</span>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-4 w-4"
                        >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </Button>
                </div>
            )}

            <div className="flex flex-row justify-between items-center space-x-4">
                <p className="text-sm w-3/4">
                    Manage your subscriptions here. You can add, edit, test and remove your subscriptions.
                </p>
                <div className="flex flex-row space-x-4 w-auto">
                    {process.env.NODE_ENV === 'development' ? (
                        <Button className="w-max" variant="outline" onClick={handleSlackInstall}>
                            Add Slack Integration
                        </Button>
                    ) : (
                        <Button className="w-max" variant="outline" asChild>
                            <Link target="_blank" to={slackInstallUrl}>
                                Add Slack Integration
                            </Link>
                        </Button>
                    )}
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="w-max">Add Subscription</Button>
                        </DialogTrigger>
                        <SubscriptionDialog onClose={() => setOpen(false)} />
                    </Dialog>
                </div>
            </div>
            <SubscriptionsTable />
        </div>
    );
}
